import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useOutletContext,
} from 'react-router-dom';
import { Control, useForm } from 'react-hook-form';
import { SendMessage } from 'react-use-websocket';
import {
  IncomingSidepanel,
  ajvResolver,
  BaseTransaction,
  ResponseError,
  responseErrorsResolver,
  PermissionEnum,
  BaseellaStatus,
  usePrevious,
  formatBalance,
  PaymentType,
  SelectItemProps,
} from '@ui/src';
import { Balance } from '@ui/src/components/transactions-sidepanels/types';
import { useAppSelector, useAppDispatch } from '@store';
import queryString from 'query-string';
import { clearStatusesState } from '../../store/statuses/slice';
import useGetPspPartnersAutocompleteList from '../../store/dictionaries/helpers/use-psp-partners-autocomplete';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import useGetPrimaryAccountsAutocompleteList from '../../store/dictionaries/helpers/use-primary-accounts-autocomplete';
import StatusesUrl from '../../types/statuses-url';
import { CheckTransactionStatus } from '../../types/check-transaction';
import {
  changePanelState,
  clearIncomingTransactionState,
} from './store/slices/transaction';
import {
  clearState,
  clearExpandedGeneralLedgerTransactionState,
} from '../transactions/store/slice';
import { CreateIncomingTransactionFormValues } from './types';
import { useLazyGetTransactionsAccountBalanceQuery } from '../../store/dictionaries/services';
import { setCollapseAllRows } from './store/slices/list';
import { useLazyGetAllAccountalancesQuery } from '../transactions/store/services';
import { MfaToken, MfaType } from '../../store/types';
import { clearSignaturesState } from '../../store/signature/slice';
import {
  ibanRegexp,
  // sortCodeRegexp,
  // swiftRegexp,
} from '../transactions/helpers/regex';
import useGetExternalAccountsList from './helpers/use-get-external-accounts-list';
import useRequests from './hooks/use-requests';
import useBaseRequests from '../transactions/hooks/use-base-requests';

import ROUTES from '../../routes';

const defaultFormValues: any = {
  acceptedIn: '',
  comment: '',
  counterpartyBankCountryScore: '',
  counterpartyCountryScore: '',
  countryScore: '',
  currencyId: '',
  documentNumber: '',
  info: '',
  intermediaryBankAddress: '',
  intermediaryBankBIC: '',
  intermediaryBankCorrespondentAccount: '',
  intermediaryBankCountry: '',
  intermediaryBankName: '',
  multipleTransactionVolumeScore: '',
  paymentType: '',
  productScore: '',
  pspPartnerAccountNumber: '',
  pspPartnerId: '',
  recipientAccountNumber: '',
  recipientRiskScore: '',
  remitterAddress: '',
  remitterBankAddress: '',
  remitterBankBIC: '',
  remitterBankCorrespondentAccount: '',
  remitterBankCountry: '',
  remitterBankName: '',
  remitterBankSortCode: '',
  remitterBankSwiftCode: '',
  remitterCity: '',
  remitterCountry: '',
  remitterFullName: '',
  senderAccountNumber: '',
  singleTransactionVolumeScore: '',
  sum: '',
  totalRiskScore: '',
  transactionScore: '',
  transferDate: '',
  depositDateOfDestinationAccount: '',
};

const formatFormDate = (date: string) => {
  const dateFormatted = new Date(date);

  dateFormatted.setHours(0);
  dateFormatted.setMinutes(0);
  dateFormatted.setSeconds(0);
  dateFormatted.setMilliseconds(0);

  return dateFormatted;
};

const schema = {
  type: 'object',
  properties: {
    transferDate: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date is required',
    },
    sum: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Amount is required',
    },
    currencyId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency is required',
    },
    paymentType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Payment type is required',
    },
    pspPartnerId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Bank partner is required',
    },
    pspPartnerAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Bank partner account is required',
    },
    recipientAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Beneficiary Account is required',
    },
    info: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Info is required',
    },
    remitterFullName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Originators Full Name is required',
    },
    // remitterCountry: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Originators country is required',
    // },
    // remitterAddress: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Originators address is required',
    // },
    // remitterBankName: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Originators Bank name is required',
    // },
    // remitterBankCountry: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Originators Bank country is required',
    // },
    // remitterBankAddress: {
    //   type: 'string',
    //   minLength: 1,
    //   errorMessage: 'Originators Bank address is required',
    // },
  },
  required: [
    'transferDate',
    'sum',
    'currencyId',
    'paymentType',
    'pspPartnerId',
    'pspPartnerAccountNumber',
    'info',
    'recipientAccountNumber',
    'remitterFullName',
    // 'remitterCountry',
    // 'remitterAddress',
    // 'remitterBankName',
    // 'remitterBankCountry',
    // 'remitterBankAddress',
  ],
};

const phoneRegex =
  /^[\\+]?[0-9]{0,3}\W?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}$/im;

const IncomingTransaction = (): React.JSX.Element => {
  const {
    transactions: {
      employee,
      chats,
      companyOfficials,
      currentEmployee,
      document: documentItem,
      documents,
      expandedGLTransaction,
      expandedGLTransactionCreditBalance,
      expandedGLTransactionDebitBalance,
      balances,
      caspList,
      transactionChangeLogs,
      transactionChangeLogsPagination,
      documentsPagination,
      transactionComments,
      transactionCommentsPagination,
    },
    incoming: {
      transaction: {
        panelIsOpen,
        transaction,
        aml,
        warnings,
        glTransactions,
        warningsInfo,
      },
      list: { takeCount },
    },
    transactionsDictionaries: {
      currencies,
      countries,
      paymentTypes,
      activePermissions,
      transactionsTypes,
      baseCurrency,
      currenciesList: dictionariesCurrenciesList,
      overdraftEnabled,
    },
    statuses: { statuses, statusLogs },
    signatures: { signatures },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const [formattedSchema, setFormattedSchema] = useState(schema);
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    SelectItemProps[]
  >([]);
  const [statusCheckError, setStatusCheckError] = useState('');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { transactionId } = useParams();
  const { search, state: locationState } = useLocation();

  const {
    control,
    formState: { dirtyFields, errors },
    reset,
    setError,
    getValues,
    setValue,
    watch,
    clearErrors,
    handleSubmit,
  } = useForm({
    resolver: ajvResolver(formattedSchema),
    defaultValues: {
      ...defaultFormValues,
    },
  });

  const [accountBalance, setAccountBalance] = useState<number>();
  const [pspPartnerAccountBalance, setPspPartnerAccountBalance] =
    useState<number>();
  const [pspPartnerAccountBalanceInfo, setPspPartnerAccountBalanceInfo] =
    useState<Balance>();
  const [createdTransactionId, setCreatedTransactionId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [amount, setAmount] = useState<string | undefined>();
  const [statusChangeError, setStatusChangeError] = useState('');
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [checkError, setCheckError] = useState('');
  const [backPath, setBackPath] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);
  const [warningActionError, setWarningActionError] = useState<
    { warningId: string; error: string } | undefined
  >(undefined);

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const fields = watch();
  const pspPartnerId = watch('pspPartnerId');
  const paymentType = watch('paymentType');
  const currencyId = watch('currencyId');
  const beneficiaryAccount = watch('recipientAccountNumber');
  const pspPartnerAccountNumber = watch('pspPartnerAccountNumber');
  const transferDate = watch('transferDate');
  const acceptedIn = watch('acceptedIn');
  const pspIntegrationAccountId = watch('pspIntegrationAccountId');

  const prevCurrencyId = usePrevious(currencyId);
  const prevPaymentType = usePrevious(paymentType);
  const prevPspPartnerId = usePrevious(pspPartnerId);
  const prevPspIntegrationAccountId = usePrevious(pspIntegrationAccountId);
  const prevBeneficiaryAccount = usePrevious(beneficiaryAccount);

  const isDirty = Object.keys(dirtyFields).length !== 0;

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    isLoading: recipientAccountsIsLoading,
    accountsList,
    setAccountsList,
    setAutocompleteList,
  } = useGetAccountsAutocompleteList(
    `&isClientAccount=true${!transactionId ? '&closed=false' : ''}`,
    'fullNumber',
  );

  const {
    externalAccountsList,
    onSearch: onSearchExternalAccounts,
    onClear: onClearExxternalAccounts,
    getExternalAccountsListAction,
    isLoading: externalAccountsListLoading,
    externalAccountsPagination,
    nextPageIsLoading: externalAccountsNextPageIsLoading,
    setExternalAccountsList,
    setExternalAccountsPagination,
  } = useGetExternalAccountsList();

  const {
    autocompleteList: pspPartners,
    onSearch: onSearchPspPartners,
    isLoading: pspPartnersIsLoading,
    getAutocompleteListAction,
  } = useGetPspPartnersAutocompleteList(
    paymentType ? `&paymentsTypes=${paymentType}` : '',
  );

  const {
    autocompleteList: pspPartnerAccounts,
    onSearch: onSearchPspPartnerAccounts,
    isLoading: pspPartnerAccountsIsLoading,
    accountsList: pspPartnersAccountsList,
    pagination,
    nextPageIsLoading,
    onScroll,
    getTransactionPspAccountByIdAction,
  } = useGetPrimaryAccountsAutocompleteList(pspPartnerId, currencyId);

  const onClearSidepanel = () => {
    dispatch(clearIncomingTransactionState());
    dispatch(clearState());
    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
  };

  const setCollapseAllRowsAction = () => {
    dispatch(setCollapseAllRows(true));
  };

  const {
    feeValue,
    setFeeValue,
    checkStatus,
    submitDisabled,
    setSubmitDisabled,
    isLoading,
    isUninitialized,
    editIsLoading,
    updateIsError,
    create,
    createSuccess,
    createInProgress,
    getTransactionsList,
    isWarningLoading,
    isWarningFetching,
    checkInProgress,
    isLoadingAml,
    applyTransactionsWarningIsLoading,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    onApplyTransactionAction,
    onClearTimer,
    internalError,
    onSubmit,
    getIncomingTransactionByIdAmlAction,
    getStatusesTransactionById,
    getTransaction,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    getIncomingTransactionWarningByIdAction,
    isWarningByIdFetching,
    isWarningByIdLoading,
    isWarningInfoLoading,
    selectedWarningId,
    transferDateWithTime,
    setTransferDateWithTime,
    accpetedInWithTime,
    setAcceptedInWithTime,
    postingDateWithTime,
    setPostingDateWithTime,
  } = useRequests({
    transactionId,
    warnings,
    transaction,
    activePermissions,
    setCheckError,
    fields,
    setError,
    setErrorMessage,
    setStatusChangeError,
    getValues,
    errors,
    isDirty,
    panelIsOpen,
    recipientAccounts,
    setStatusCheckError,
    currencyId: fields.currencyId,
    sum: fields.sum,
    paymentType: fields.paymentType,
    setWarningActionError,
  });

  const handleClose = useCallback(() => {
    setCheckError('');
    dispatch(clearIncomingTransactionState());
    dispatch(clearState());
    dispatch(clearSignaturesState());
    setUploadedFilesError([]);

    localStorage.removeItem('noScheme');

    onClearTimer(true);

    setTimeout(() => {
      if (backPath) {
        navigate(backPath);
      } else {
        navigate(`${ROUTES.INCOMING}${search || ''}`);
      }

      setBackPath('');
    }, 100);
  }, [backPath, search]);

  const {
    imgLink,
    deleteTransactionError,
    errorMessageSignature,
    setErrorMessageSignature,
    signaturePopupVisible,
    setSignaturePopupVisible,
    statusChangeInProgress,
    statusPopupVisible,
    setStatusPopupVisible,
    rejectPopupVisible,
    setRejectPopupVisible,
    deleteTransactionInProgress,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    commentInProgress,
    documentCategoryChangeInProgress,
    documentStatusChangeInProgress,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    documentsIsLoading,
    addDocumentInProgress,
    fileUploadIsSuccess,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    glTransactionIsLoading,
    glTransactionIsFetching,
    glIsLoading,
    fileUploadInProgress,
    transactionChatsIsLoading,
    cofIsLoading,
    chatCreateInProgress,
    isLoadingSignatures,
    createSignatureInProgress,
    signatureIsSuccess,
    sendCodeInProgress,
    validateCodeInProgress,
    changeStatusIsSuccess,
    changeStatusIsLoading,
    isLoadingLogs,
    onDeleteTransaction,
    getTransactionDocumentyIdAction,
    onGlTransactionClick,
    onUploadDocument,
    onCreateTransactionChat,
    createChatWithMessage,
    onSendCode,
    validateCodeAction,
    getLogsTransactionByIdAction,
    onClearRequestTimer,
    onChangeStatusPopup,
    onSignatureCancelClick,
    onChangeStatus,
    getTransationDocumentsAction,
    getGeneralLedgerTransactionValuesAction,
    getTransactionsChatsAction,
    getCompanyOfficialsAction,
    getSignaturesTransactionByIdAction,
    onReject,
    onDeleteDocument,
    onMoveDocument,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onChangeCustomerDocumentsStatus,
    sendCodeRetry,
    sendCode,
    onClearRequestTimeout,
    getTransactionChangeLogsAction,
    changeLogsIsLoading,
    changeLogsIsFetching,
    documentsIsFetching,
    attachedDocumentsErrors,
    setAttachedDocumentsErrors,
    uploadedDocumentsErrors,
    setUploadedDocumentsErrors,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
  } = useBaseRequests({
    setError,
    transactionId,
    transaction,
    currentEmployee,
    sendMessage,
    setErrorMessage,
    createdTransactionId,
    statusUrl: StatusesUrl.INCOMING,
    setStatusChangeError,
    onSubmit,
    getTransactionsList,
    getStatusesTransactionById,
    onClearTimer,
    getTransaction,
    reset,
    handleClose,
    onClear: onClearSidepanel,
    chatTitle: 'RFI: Incoming transaction',
    takeCount,
    search,
    transactionListUrl: ROUTES.INCOMING_TRANSACTION,
    errors,
    isDirty,
    documents,
    documentItem,
    setCollapseAllRows: setCollapseAllRowsAction,
    updateIsError,
    paymentType,
    expandedGLTransaction,
    setUploadedFilesError,
    transactionUrl: 'incoming',
  });

  const prevSignaturePopupVisible = usePrevious(signaturePopupVisible);

  const onDuplicate = () => {
    onClearRequestTimer();
    onClearTimer(true);
    setCheckError('');
    dispatch(clearIncomingTransactionState());
    dispatch(clearState());
    setUploadedFilesError([]);
    reset({});

    setTimeout(() => {
      navigate(`${ROUTES.INCOMING}${search || ''}`);
    }, 100);

    setTimeout(() => {
      navigate(`${ROUTES.INCOMING_TRANSACTION_CREATE}${search || ''}`, {
        state: {
          ...transaction,
          duplicate: true,
        },
      });
    }, 200);
  };

  const sendCodeAction = handleSubmit(async (values) => {
    setErrorMessage('');

    let createResponse;
    let requestAvailable = true;

    if (values.paymentType === PaymentType.CASH_OVER_COUNTER) {
      if (!values.collectorContact) {
        requestAvailable = false;
        setError('collectorContact', {
          type: 'custom',
          message: 'Collector Contact Is Required',
        });
      } else {
        clearErrors(['collectorContact']);

        if (!phoneRegex.test(values.collectorContact.replace(/\s/g, ''))) {
          requestAvailable = false;

          setError('collectorContact', {
            type: 'custom',
            message: 'Invalid Phone Number',
          });
        } else {
          clearErrors(['collectorContact']);

          requestAvailable = true;
        }
      }
    }

    if (!transactionId && !Object.keys(errors).length && requestAvailable) {
      const body: {
        paymentType: string;
        sum: number;
        senderAccountNumber: string;
        recipientAccountNumber?: string;
        currencyId: string;
        transferDate?: string;
        acceptedIn?: string;
        info?: string;
        documentNumber?: string;
        remitterFullName?: string;
        remitterBankSortCode?: string;
        remitterBankSwiftCode?: string;
        remitterAddress?: string;
        remitterCity?: string;
        remitterCountry?: string;
        remitterBankName?: string;
        remitterBankCountry?: string;
        remitterBankAddress?: string;
        remitterBankCorrespondentAccount?: string;
        intermediaryBankBIC?: string;
        intermediaryBankCorrespondentAccount?: string;
        intermediaryBankName?: string;
        intermediaryBankCountry?: string;
        intermediaryBankAddress?: string;
        pspPartnerId?: string;
        pspPartnerAccountNumber?: string;
        remitterBankBIC?: string;
        depositDateOfDestinationAccount?: string;
        blockchainTransactionHashId?: string;
        caspId?: string;
        pspIntegrationAccountId?: string;
        collectorContact?: string;
        customPassword?: string;
        aba?: string;
      } = {
        paymentType: values.paymentType || '',
        sum: Number(values.sum),
        senderAccountNumber: values.senderAccountNumber,
        currencyId: values.currencyId,
      };

      if (values?.pspIntegrationAccountId) {
        body.pspIntegrationAccountId = values.pspIntegrationAccountId;
      }

      if (values.recipientAccountNumber) {
        body.recipientAccountNumber = values.recipientAccountNumber;
      }

      if (values.pspPartnerAccountNumber) {
        body.pspPartnerAccountNumber = values.pspPartnerAccountNumber;
      }

      if (values.pspPartnerId) {
        body.pspPartnerId = values.pspPartnerId;
      }

      if (values.transferDate) {
        const date = new Date(values.transferDate);

        if (transferDateWithTime) {
          body.transferDate = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);

          body.transferDate = date.toISOString();
        }
      }

      if (values?.depositDateOfDestinationAccount) {
        const date = new Date(values.depositDateOfDestinationAccount);

        if (postingDateWithTime) {
          body.depositDateOfDestinationAccount = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);

          body.depositDateOfDestinationAccount = date.toISOString();
        }
      }

      if (values.acceptedIn) {
        const date = new Date(values.acceptedIn);

        if (accpetedInWithTime) {
          body.acceptedIn = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);

          body.acceptedIn = date.toISOString();
        }
      }

      if (values.info) {
        body.info = values.info;
      }

      if (values.documentNumber) {
        body.documentNumber = values.documentNumber;
      }

      if (values.remitterFullName) {
        body.remitterFullName = values.remitterFullName;
      }

      if (values.remitterBankSortCode) {
        body.remitterBankSortCode = values.remitterBankSortCode;
      }

      if (values.remitterBankSwiftCode) {
        body.remitterBankSwiftCode = values.remitterBankSwiftCode;
      }

      if (values.remitterAddress) {
        body.remitterAddress = values.remitterAddress;
      }

      if (values.remitterCity) {
        body.remitterCity = values.remitterCity;
      }

      if (values.remitterCountry) {
        body.remitterCountry = values.remitterCountry;
      }

      if (values.remitterBankName) {
        body.remitterBankName = values.remitterBankName;
      }

      if (values.remitterBankCountry) {
        body.remitterBankCountry = values.remitterBankCountry;
      }

      if (values.remitterBankAddress) {
        body.remitterBankAddress = values.remitterBankAddress;
      }

      if (values.remitterBankCorrespondentAccount) {
        body.remitterBankCorrespondentAccount =
          values.remitterBankCorrespondentAccount;
      }

      if (values.intermediaryBankBIC) {
        body.intermediaryBankBIC = values.intermediaryBankBIC;
      }

      if (values.remitterBankBIC) {
        body.remitterBankBIC = values.remitterBankBIC;
      }

      if (values.intermediaryBankCorrespondentAccount) {
        body.intermediaryBankCorrespondentAccount =
          values.intermediaryBankCorrespondentAccount;
      }

      if (values.intermediaryBankName) {
        body.intermediaryBankName = values.intermediaryBankName;
      }

      if (values.intermediaryBankCountry) {
        body.intermediaryBankCountry = values.intermediaryBankCountry;
      }

      if (values.intermediaryBankAddress) {
        body.intermediaryBankAddress = values.intermediaryBankAddress;
      }

      if (values.remitterCity) {
        body.remitterCity = values.remitterCity;
      }

      if (values?.blockchainTransactionHashId) {
        body.blockchainTransactionHashId = values?.blockchainTransactionHashId;
      }

      if (values?.caspId) {
        body.caspId = values.caspId;
      }

      if (values.collectorContact) {
        body.collectorContact = values.collectorContact;
      }

      if (values.customPassword) {
        body.customPassword = values.customPassword;
      }

      if (values.aba) {
        body.aba = values.aba;
      }

      createResponse = (await create(
        body as CreateIncomingTransactionFormValues,
      )) as {
        data?: { data: BaseTransaction };
        error: ResponseError;
      };
    }

    if (createResponse && createResponse.error) {
      responseErrorsResolver({
        errors: createResponse.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else if (createResponse && createResponse.data) {
      if (createResponse && createResponse.data) {
        setCreatedTransactionId(createResponse.data.data.id);
      }

      if (currentEmployee?.canSignTransactions) {
        const response = (await sendCode({ mfa: MfaType.TOTP })) as {
          data: { data: MfaToken };
        };

        if (response && response.data && response.data.data) {
          setSignaturePopupVisible(true);

          const expireTime = Math.round(
            new Date(response.data.data.expiresAt).getTime() - Date.now(),
          );

          sendCodeRetry(expireTime);
        }
      }
    }
  });

  const [
    getAccountBalance,
    {
      isLoading: accountBalanceIsLoading,
      isFetching: accountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [
    getAllAccountBalances,
    {
      isLoading: allAccountBalancesIsLoading,
      isFetching: allAccountBalancesisFetching,
    },
  ] = useLazyGetAllAccountalancesQuery();

  const [
    getPspPartnerAccountBalance,
    {
      isLoading: pspPartnerAccountBalanceIsLoading,
      isFetching: pspPartnerAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    switch (name) {
      case 'recipientAccountNumber':
        onSearchRecipientAccounts(searchAutocomplete);
        break;
      case 'pspPartnerId':
        onSearchPspPartners(searchAutocomplete);
        break;
      case 'pspPartnerAccountNumber':
        onSearchPspPartnerAccounts(searchAutocomplete);
        break;
      default:
        break;
    }
  };

  const getAllAccountBalancesAction = useCallback(
    (id: string) => {
      getAllAccountBalances({
        id,
      });
    },
    [beneficiaryAccount],
  );

  const getAccountBalanceAction = useCallback(
    async (number?: string) => {
      const currentAccount = accountsList.find(
        (item) => item.fullNumber === beneficiaryAccount || number,
      );

      if (currentAccount && currencyId) {
        const response = (await getAccountBalance({
          id: currentAccount.id,
          currencyId: currencyId as string,
        })) as { data: { data: { edges: { node: { value: number } }[] } } };

        if (response?.data?.data) {
          setAccountBalance(response.data.data.edges[0]?.node?.value);
        }
      }
    },
    [beneficiaryAccount, currencyId, accountsList],
  );

  const getExternalAccountBalanceAction = useCallback(
    async (id?: string) => {
      const currentAccount = externalAccountsList?.find(
        (item) => item.id === pspIntegrationAccountId || id,
      );

      if (currentAccount && currencyId) {
        const response = (await getAccountBalance({
          id: currentAccount.account.id,
          currencyId: currencyId as string,
        })) as { data: { data: { edges: { node: { value: number } }[] } } };

        if (response?.data?.data) {
          setAccountBalance(response.data.data.edges[0]?.node?.value);
        }
      }
    },
    [pspIntegrationAccountId, currencyId, externalAccountsList],
  );

  const getPspPartnerAccountBalanceAction = useCallback(async () => {
    const currentAccount = pspPartnersAccountsList.find(
      (item) => item?.account?.fullNumber === pspPartnerAccountNumber,
    );

    if (currentAccount) {
      const response = (await getPspPartnerAccountBalance({
        id: currentAccount?.account?.id as string,
        currencyId,
      })) as { data: { data: { edges: { node: Balance }[] } } };

      if (response.data.data) {
        setPspPartnerAccountBalance(response.data.data?.edges[0]?.node.value);
        setPspPartnerAccountBalanceInfo(response.data.data?.edges[0]?.node);
      }
    }
  }, [pspPartnerAccountNumber, currencyId, pspPartnersAccountsList]);

  useEffect(() => {
    if (acceptedIn) {
      const formattedAcceptedIn = formatFormDate(acceptedIn);
      const formattedTransferDate = formatFormDate(transferDate);

      if (transferDate && formattedAcceptedIn > formattedTransferDate) {
        setError('acceptedIn', {
          type: 'custom',
          message: 'Accepted In date cannot be later than Creation Date',
        });
      } else {
        clearErrors('acceptedIn');
      }
    }
  }, [acceptedIn, transferDate]);

  useEffect(() => {
    if (transferDate) {
      const formattedTransferDate = formatFormDate(transferDate);
      const formattedCurrentDate = formatFormDate(new Date().toISOString());

      if (transferDate && formattedTransferDate > formattedCurrentDate) {
        setError('transferDate', {
          type: 'custom',
          message: 'Creation Date cannot be later than current date',
        });
      } else {
        clearErrors('transferDate');
      }
    }
  }, [transferDate]);

  useEffect(() => {
    let nextSchema: any = schema;

    const withoutScheme = localStorage.getItem('noScheme');

    if (
      fields.currencyId &&
      prevCurrencyId &&
      prevCurrencyId !== fields.currencyId
    ) {
      clearErrors();
    }

    if ((withoutScheme && withoutScheme === 'false') || !withoutScheme) {
      if (
        paymentType === PaymentType.FASTER_PAYMENT ||
        paymentType === PaymentType.BACS
      ) {
        nextSchema = {
          ...schema,
          properties: {
            ...schema.properties,
            // remitterBankSortCode: {
            //   type: 'string',
            //   regexp: sortCodeRegexp.toString(),
            //   errorMessage: 'Sort Code empty or incorrect',
            // },
            senderAccountNumber: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Originators account is required',
            },
            remitterCountry: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Originators country is required',
            },
          },
          required: [
            ...schema.required,
            // 'remitterBankSortCode',
            'senderAccountNumber',
            'remitterCountry',
          ],
        };

        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.SWIFT ||
            prevPaymentType === PaymentType.SEPA_SCT ||
            prevPaymentType === PaymentType.SEPA_SCT_INST ||
            prevPaymentType === PaymentType.TARGET_2 ||
            prevPaymentType === PaymentType.FEDWIRE)
        ) {
          clearErrors();

          setValue('remitterBankBIC', '');
          setValue('intermediaryBankBIC', '');
          setValue('intermediaryBankCorrespondentAccount', '');
          setValue('intermediaryBankCountry', '');
          setValue('intermediaryBankAddress', '');
          setValue('intermediaryBankName', '');
          setValue('aba', '');
        }
      } else if (paymentType === PaymentType.SWIFT) {
        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.FASTER_PAYMENT ||
            prevPaymentType === PaymentType.BACS ||
            prevPaymentType === PaymentType.FEDWIRE)
        ) {
          clearErrors();

          setValue('remitterBankSortCode', '');
          setValue('aba', '');
        }

        nextSchema = {
          ...schema,
          properties: {
            ...schema.properties,
            // remitterBankBIC: {
            //   type: 'string',
            //   regexp: swiftRegexp.toString(),
            //   errorMessage: 'BIC empty or incorrect',
            // },
            senderAccountNumber: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Originators account is required',
            },
            remitterCountry: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Originators country is required',
            },
          },
          required: [
            ...schema.required,
            // 'remitterBankBIC',
            'senderAccountNumber',
            'remitterCountry',
          ],
        };

        if (
          fields.intermediaryBankBIC ||
          fields.intermediaryBankCorrespondentAccount ||
          fields.intermediaryBankCountry ||
          fields.intermediaryBankAddress ||
          fields.intermediaryBankName
        ) {
          nextSchema = {
            ...schema,
            properties: {
              ...schema.properties,
              // intermediaryBankBIC: {
              //   type: 'string',
              //   regexp: swiftRegexp.toString(),
              //   errorMessage: 'BIC empty or incorrect',
              // },
              // intermediaryBankCorrespondentAccount: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Correspondent Account is required',
              // },
              // intermediaryBankCountry: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Country is required',
              // },
              // intermediaryBankAddress: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Address is required',
              // },
              // intermediaryBankName: {
              //   type: 'string',
              //   minLength: 1,
              //   errorMessage: 'Bank Name is required',
              // },
              remitterCountry: {
                type: 'string',
                minLength: 1,
                errorMessage: 'Originators country is required',
              },
            },
            required: [
              ...schema.required,
              // 'intermediaryBankBIC',
              // 'intermediaryBankCorrespondentAccount',
              // 'intermediaryBankCountry',
              // 'intermediaryBankAddress',
              // 'intermediaryBankName',
              'remitterCountry',
            ],
          };
        }
      } else if (
        paymentType === PaymentType.SEPA_SCT ||
        paymentType === PaymentType.SEPA_SCT_INST ||
        paymentType === PaymentType.TARGET_2
      ) {
        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.FASTER_PAYMENT ||
            prevPaymentType === PaymentType.BACS ||
            prevPaymentType === PaymentType.SWIFT ||
            paymentType === PaymentType.FEDWIRE)
        ) {
          clearErrors();

          setValue('remitterBankSortCode', '');
          setValue('intermediaryBankBIC', '');
          setValue('intermediaryBankCorrespondentAccount', '');
          setValue('intermediaryBankCountry', '');
          setValue('intermediaryBankAddress', '');
          setValue('intermediaryBankName', '');
          setValue('aba', '');
        }

        nextSchema = {
          ...schema,
          properties: {
            ...schema.properties,
            senderAccountNumber: {
              type: 'string',
              regexp: ibanRegexp.toString(),
              errorMessage: 'IBAN empty or incorrect',
            },
            remitterCountry: {
              type: 'string',
              minLength: 1,
              errorMessage: 'Originators country is required',
            },
            // remitterBankBIC: {
            //   type: 'string',
            //   regexp: swiftRegexp.toString(),
            //   errorMessage: 'BIC empty or incorrect',
            // },
          },
          required: [
            ...schema.required,
            'senderAccountNumber',
            'remitterCountry',
            // 'remitterBankBIC',
          ],
        };
      } else if (paymentType === PaymentType.FEDWIRE) {
        if (
          prevPaymentType &&
          (prevPaymentType === PaymentType.FASTER_PAYMENT ||
            prevPaymentType === PaymentType.BACS ||
            prevPaymentType === PaymentType.SWIFT ||
            prevPaymentType === PaymentType.SEPA_SCT ||
            prevPaymentType === PaymentType.SEPA_SCT_INST)
        ) {
          clearErrors();

          setValue('remitterBankSortCode', '');
          setValue('intermediaryBankBIC', '');
          setValue('intermediaryBankCorrespondentAccount', '');
          setValue('intermediaryBankCountry', '');
          setValue('intermediaryBankAddress', '');
          setValue('intermediaryBankName', '');
          setValue('remitterBankBIC', '');
        }

        nextSchema = {
          ...schema,
          properties: {
            ...schema.properties,
            aba: {
              type: 'string',
              minLength: 1,
              errorMessage: 'ABA is required',
            },
          },
          required: [...schema.required, 'aba'],
        };
      }
    } else {
      nextSchema = {
        type: 'object',
        properties: {
          transferDate: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Date is required',
          },
          sum: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Amount is required',
          },
          currencyId: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Currency is required',
          },
          paymentType: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Payment type is required',
          },
          pspPartnerId: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Bank partner is required',
          },
          pspPartnerAccountNumber: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Bank partner account is required',
          },
          recipientAccountNumber: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Beneficiary Account is required',
          },
          info: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Info is required',
          },
        },
        required: [
          'transferDate',
          'sum',
          'currencyId',
          'paymentType',
          'pspPartnerId',
          'pspPartnerAccountNumber',
          'info',
          'recipientAccountNumber',
        ],
      };
    }

    setFormattedSchema(nextSchema);
  }, [
    paymentType,
    prevPaymentType,
    currencyId,
    prevCurrencyId,
    setValue,
    fields.intermediaryBankBIC,
    fields.intermediaryBankCorrespondentAccount,
    fields.intermediaryBankCountry,
    fields.intermediaryBankAddress,
    fields.intermediaryBankName,
  ]);

  useEffect(() => {
    if (
      transaction &&
      transaction?.pspPartnerAccountNumber &&
      pspPartnerAccounts?.length
    ) {
      const currentAccount = pspPartnerAccounts?.find(
        (item) => item.value === transaction?.pspPartnerAccountNumber,
      );

      if (!currentAccount) {
        getTransactionPspAccountByIdAction(
          transaction?.pspPartnerAccountNumber,
        );
      }
    }
  }, [transaction, pspPartnerAccounts]);

  useEffect(() => {
    if (
      !transactionId &&
      locationState &&
      locationState.duplicate &&
      pspPartnerAccountNumber &&
      pspPartnerAccounts?.length
    ) {
      const currentAccount = pspPartnerAccounts?.find(
        (item) => item.value === pspPartnerAccountNumber,
      );

      if (!currentAccount) {
        getTransactionPspAccountByIdAction(
          locationState?.pspPartnerAccountNumber,
        );
      }
    }
  }, [locationState, pspPartnerAccounts, pspPartnerAccountNumber]);

  let feeText;

  if (feeValue === undefined) {
    feeText = 'Fee will be calculated after filling in the required fields';
  } else if (fields.currencyId) {
    feeText = `Fee: ${formatBalance(feeValue)} ${
      currencies?.find((item) => item.value === fields.currencyId)?.name
    }`;
  }

  useEffect(() => {
    if (balances && currencies && baseCurrency) {
      const currenciesList: SelectItemProps[] = [];

      if (balances?.find((item) => item.currencyId === baseCurrency.id)) {
        currenciesList.push({
          name: baseCurrency.name,
          value: baseCurrency.id,
          suffix: baseCurrency.description,
        });
      }

      currencies
        ?.filter((item) => item.value !== baseCurrency?.id)
        ?.forEach((item) => {
          if (balances.find((balance) => balance.currencyId === item.value)) {
            currenciesList.push(item);
          }
        });

      setAvailableCurrencies(currenciesList);
    }
  }, [balances, currencies, baseCurrency]);

  useEffect(() => {
    if (
      currencyId &&
      beneficiaryAccount &&
      accountsList &&
      !locationState?.duplicate
    ) {
      getAccountBalanceAction();
    }
  }, [currencyId, beneficiaryAccount, accountsList, locationState]);

  useEffect(() => {
    if (
      currencyId &&
      pspIntegrationAccountId &&
      externalAccountsList &&
      !locationState?.duplicate
    ) {
      getExternalAccountBalanceAction();
    }
  }, [currencyId, externalAccountsList, pspIntegrationAccountId]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      !pspIntegrationAccountId
    ) {
      getAccountBalanceAction();
    }
  }, [currencyId, prevCurrencyId, pspIntegrationAccountId]);

  useEffect(() => {
    if (
      pspIntegrationAccountId &&
      currencyId &&
      ((prevCurrencyId !== null && currencyId !== prevCurrencyId) ||
        (prevPspIntegrationAccountId !== null &&
          pspIntegrationAccountId !== prevPspIntegrationAccountId) ||
        (beneficiaryAccount &&
          prevBeneficiaryAccount !== null &&
          beneficiaryAccount !== prevBeneficiaryAccount))
    ) {
      getExternalAccountBalanceAction();
    }
  }, [
    currencyId,
    prevCurrencyId,
    pspIntegrationAccountId,
    prevPspIntegrationAccountId,
    beneficiaryAccount,
    prevBeneficiaryAccount,
  ]);

  useEffect(() => {
    if (
      currencyId &&
      beneficiaryAccount &&
      balances &&
      !balances?.find((item) => item.currencyId === currencyId)
    ) {
      setValue('currencyId', '');
    }
  }, [currencyId, balances, beneficiaryAccount]);

  useEffect(() => {
    if (
      beneficiaryAccount &&
      accountsList &&
      !transactionId &&
      !pspIntegrationAccountId
    ) {
      const currentAccount = accountsList.find(
        (item) => item.fullNumber === beneficiaryAccount,
      );

      if (currentAccount) {
        getAllAccountBalancesAction(currentAccount.id);
      }
    }
  }, [
    beneficiaryAccount,
    accountsList,
    transactionId,
    pspIntegrationAccountId,
  ]);

  useEffect(() => {
    if (pspIntegrationAccountId && externalAccountsList) {
      const currentExternal = externalAccountsList?.find(
        (item) => item.id === pspIntegrationAccountId,
      );

      if (currentExternal) {
        getAllAccountBalancesAction(currentExternal.account.id);
      }
    }
  }, [pspIntegrationAccountId, externalAccountsList]);

  useEffect(() => {
    if (currencyId && pspPartnerAccountNumber && pspPartnersAccountsList) {
      getPspPartnerAccountBalanceAction();
    }
  }, [currencyId, pspPartnerAccountNumber, pspPartnersAccountsList]);

  useEffect(() => {
    if (transactionId) {
      dispatch(changePanelState({ state: true, transactionId }));
    } else {
      dispatch(changePanelState({ state: true }));
    }

    return () => {
      dispatch(clearIncomingTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});
    };
  }, [dispatch, transactionId, activePermissions]);

  useEffect(() => {
    if (
      fields.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
      transactionId &&
      accountsList &&
      beneficiaryAccount &&
      !pspIntegrationAccountId
    ) {
      const currentAccount = accountsList?.find(
        (item) => item.fullNumber === beneficiaryAccount,
      );

      if (currentAccount && currentAccount.id) {
        getAllAccountBalancesAction(currentAccount.id);
      }
    }
  }, [transactionId, fileUploadInProgress, accountsList, beneficiaryAccount]);

  useEffect(() => {
    if (transaction) {
      setSubmitDisabled(false);

      if (transaction?.recipientAccount) {
        getAllAccountBalancesAction(
          transaction?.recipientAccount?.id as string,
        );
      }

      reset({
        ...transaction,
        sum: transaction.sum.toString(),
      });

      if (transaction.sum) {
        setAmount(transaction.sum.toString());
      }

      if (transaction.feeAmount) {
        setFeeValue(transaction.feeAmount);
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (transaction?.recipientAccount) {
      setAccountsList([transaction?.recipientAccount]);
      setAutocompleteList([
        {
          name: transaction?.recipientAccount.description,
          description: '',
          value: transaction?.recipientAccount.fullNumber,
          suffix: `${transaction?.recipientAccount.fullNumber} ${
            // eslint-disable-next-line no-nested-ternary
            overdraftEnabled
              ? transaction?.recipientAccount?.overdraftEnabled
                ? '(Overdraft)'
                : ''
              : ''
          }`,
        },
      ]);
    }
  }, [transaction]);

  useEffect(() => {
    if (
      !transactionId &&
      ((paymentType && !prevPaymentType) ||
        (paymentType && prevPaymentType && paymentType !== prevPaymentType))
    ) {
      getAutocompleteListAction();
    }
  }, [paymentType, prevPaymentType, transactionId]);

  useEffect(() => {
    if (transactionId && transaction && paymentType) {
      getAutocompleteListAction();
    }
  }, [transaction, transactionId, paymentType]);

  useEffect(() => {
    if (
      changeStatusIsSuccess &&
      (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
        transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
        transaction?.baseellaStatus === BaseellaStatus.REJECTED)
    ) {
      getTransactionsList(
        `?take=${takeCount?.toString() || '25'}${search.replace('?', '&')}`,
      );
    }

    if (
      (signatureIsSuccess && !transactionId) ||
      (changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED))
    ) {
      dispatch(clearIncomingTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});

      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);

      if (
        changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED)
      ) {
        setTimeout(() => {
          getTransactionsList(
            `?take=${takeCount?.toString() || '25'}${search.replace('?', '&')}`,
          );
        }, 100);
      }
    }
  }, [transaction, signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (transactionId && signatureIsSuccess) {
      onClearTimer(true);
      onClearRequestTimeout();
      setSignaturePopupVisible(false);

      setTimeout(() => {
        getTransactionsList(
          `?take=${takeCount?.toString() || '25'}${search.replace('?', '&')}`,
        );
      }, 100);

      getTransaction();
      getSignaturesTransactionByIdAction();
    }
  }, [transactionId, signatureIsSuccess]);

  useEffect(() => {
    if (currencyId && paymentTypes) {
      const nextPaymentTypes: SelectItemProps[] = [];

      if (transactionId) {
        paymentTypes?.forEach((item) => {
          if (
            item?.currencies?.edges?.find(
              (currency) => currency.node.id === currencyId,
            )
          ) {
            nextPaymentTypes.push({
              name: item.name,
              value: item.paymentType,
            });
          }
        });
      } else {
        paymentTypes
          ?.filter((item) => item.enabled)
          ?.forEach((item) => {
            if (
              item?.currencies?.edges?.find(
                (currency) => currency.node.id === currencyId,
              )
            ) {
              nextPaymentTypes.push({
                name: item.name,
                value: item.paymentType,
              });
            }
          });
      }

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [currencyId, paymentTypes, transactionId]);

  useEffect(() => {
    if (
      currentEmployee &&
      !currentEmployee.canSignTransactions &&
      createSuccess
    ) {
      handleClose();
      setAttachedDocumentsErrors(undefined);
      setUploadedDocumentsErrors([]);
    }
  }, [currentEmployee, createSuccess]);

  useEffect(() => {
    if (
      currencyId &&
      paymentType &&
      prevCurrencyId &&
      prevPaymentType &&
      (currencyId !== prevCurrencyId || paymentType !== prevPaymentType)
    ) {
      setValue('pspPartnerId', undefined);
      setValue('pspPartnerAccountNumber', '');
    }
  }, [currencyId, prevCurrencyId, paymentType, prevPaymentType]);

  useEffect(() => {
    if (
      prevPspPartnerId &&
      prevPspPartnerId !== undefined &&
      prevPspPartnerId !== null &&
      ((pspPartnerId && pspPartnerId !== prevPspPartnerId) ||
        (prevPspPartnerId && !pspPartnerId))
    ) {
      setValue('pspPartnerAccountNumber', '');
    }
  }, [pspPartnerId, prevPspPartnerId]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      !transactionId
    ) {
      setValue('paymentType', '');
      setSubmitDisabled(true);
    }
  }, [currencyId, prevCurrencyId, transactionId]);

  useEffect(() => {
    if (transactionId && isDirty) {
      onClearTimer();
    }
  }, [isDirty, transactionId]);

  useEffect(() => {
    if (!prevSignaturePopupVisible && errorMessageSignature) {
      setErrorMessageSignature('');
    }
  }, [prevSignaturePopupVisible, errorMessageSignature]);

  useEffect(() => {
    if (locationState && locationState.duplicate && !fields.currencyId) {
      if (!fields.recipientAccountNumber) {
        onSearchAutocomplete(
          'recipientAccountNumber',
          locationState.recipientAccountNumber,
        );
      }

      reset({
        recipientAccountNumber: locationState.recipientAccountNumber,
        sum: locationState?.sum?.toString(),
        currencyId: locationState.currencyId,
        paymentType: locationState.paymentType,
        pspPartnerId: locationState.pspPartnerId,
        pspPartnerAccountNumber: locationState.pspPartnerAccountNumber,
        documentNumber: locationState.documentNumber,
        info: locationState.info,
        senderAccountNumber: locationState.senderAccountNumber || '',
        remitterFullName: locationState.remitterFullName || '',
        remitterCountry: locationState.remitterCountry || '',
        remitterCity: locationState.remitterCity || '',
        remitterAddress: locationState.remitterAddress || '',
        remitterBankBIC: locationState.remitterBankBIC || '',
        remitterBankSortCode: locationState.remitterBankSortCode || '',
        remitterBankName: locationState.remitterBankName || '',
        remitterBankCountry: locationState.remitterBankCountry || '',
        remitterBankAddress: locationState.remitterBankAddress || '',
        intermediaryBankBIC: locationState.intermediaryBankBIC || '',
        intermediaryBankCorrespondentAccount:
          locationState.intermediaryBankCorrespondentAccount || '',
        intermediaryBankName: locationState.intermediaryBankName || '',
        intermediaryBankCountry: locationState.intermediaryBankCountry || '',
        intermediaryBankAddress: locationState.intermediaryBankAddress || '',
        caspId: locationState?.caspId || '',
        aba: locationState?.aba || '',
      });

      setAmount(locationState?.sum?.toString());
    }

    if (
      locationState &&
      locationState.duplicate &&
      accountsList.length &&
      !locationState.pspInegrationAccountId
    ) {
      getAccountBalanceAction(locationState.recipientAccountNumber);
    }

    if (
      locationState &&
      locationState.duplicate &&
      externalAccountsList?.length &&
      locationState.pspInegrationAccountId
    ) {
      getExternalAccountBalanceAction(locationState.pspInegrationAccountId);
    }
  }, [locationState, accountsList, externalAccountsList]);

  const isLoadingSidepanel = useMemo(() => {
    let res = true;

    let isLoadingSidepanelVariables =
      !isLoading && !isUninitialized && !isWarningLoading;

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INCOMING_EDIT_ACTION,
      )
    ) {
      isLoadingSidepanelVariables =
        isLoadingSidepanelVariables &&
        !isLoadingStatuses &&
        !isUninitializedStatuses;
    }

    if (transactionId) {
      if (isLoadingSidepanelVariables) {
        res = false;
      }
    } else {
      res = false;
    }

    return res;
  }, [
    transactionId,
    isLoading,
    isLoadingStatuses,
    isUninitialized,
    isUninitializedStatuses,
    activePermissions,
    isWarningLoading,
  ]);

  useEffect(() => {
    if (transaction && transaction?.pspIntegrationAccount) {
      setExternalAccountsList([transaction?.pspIntegrationAccount]);
      setExternalAccountsPagination({
        endCursor: transaction?.pspIntegrationAccount.id,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: transaction?.pspIntegrationAccount.id,
        totalCount: 1,
      });
    }
  }, [transaction]);

  useEffect(() => {
    if (locationState && !locationState.duplicate) {
      setBackPath(locationState);
    }
  }, [locationState]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed && parsed?.noScheme && parsed?.noScheme === 'true') {
        localStorage.setItem('noScheme', 'true');
      }
    }
  }, [search]);

  const onSendCodeAction = !transactionId ? sendCodeAction : onSendCode;

  return (
    <IncomingSidepanel
      enableEdit
      isLoading={
        isLoadingSidepanel ||
        isWarningLoading ||
        (!warnings && isWarningFetching) ||
        Boolean(!transaction && transactionId) ||
        isWarningInfoLoading ||
        Boolean(!warningsInfo && transactionId)
      }
      sidepanelExpandedKey="incoming-transaction"
      internalError={internalError}
      panelIsOpen={panelIsOpen}
      handleClose={() => {
        handleClose();
        setAttachedDocumentsErrors(undefined);
        setUploadedDocumentsErrors([]);
      }}
      title={
        transactionId ? 'Incoming Transaction' : 'New Incoming Transaction'
      }
      handleSubmit={onSubmit}
      control={control as Control<any>}
      statusesInfo={transactionId ? statuses : undefined}
      onRollbackStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_INCOMING_REVERSE_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onNextStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_INCOMING_EDIT_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onChangeStatusPopup={onChangeStatusPopup}
      setStatusPopupVisible={setStatusPopupVisible}
      statusPopupVisible={statusPopupVisible}
      isCreateForm={!transactionId}
      isDisabledSaveButton={
        !isDirty ||
        editIsLoading ||
        createInProgress ||
        submitDisabled ||
        sendCodeInProgress ||
        checkInProgress ||
        checkStatus === CheckTransactionStatus.RED ||
        applyTransactionsWarningIsLoading
      }
      createAllowed={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INCOMING_CREATE_ACTION,
      )}
      isEditable={
        transactionId
          ? activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INCOMING_EDIT_ACTION,
            )
          : activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INCOMING_CREATE_ACTION,
            )
      }
      transactionInfo={transaction}
      amlInfo={aml}
      currencies={availableCurrencies}
      recipientAccounts={recipientAccounts}
      pspPartnerAccounts={pspPartnerAccounts.sort(
        (a, b) =>
          Number((a.value as string).replaceAll('.', '')) -
          Number((b.value as string).replaceAll('.', '')),
      )}
      pspPartners={pspPartners}
      paymentsTypes={filteredPaymentTypes}
      countries={countries}
      errors={errors && Object.keys(errors).length ? errors : undefined}
      onSearchAutocomplete={onSearchAutocomplete}
      recipientAccountsIsLoading={recipientAccountsIsLoading}
      pspPartnersIsLoading={pspPartnersIsLoading}
      pspPartnerAccountsIsLoading={pspPartnerAccountsIsLoading}
      paymentTypeIsFilled={Boolean(paymentType) && Boolean(currencyId)}
      pspPartnerIsFilled={Boolean(pspPartnerId)}
      baseellaStatus={transaction?.baseellaStatus}
      paymentType={paymentType}
      logs={statusLogs}
      feeText={feeText}
      warnings={warnings}
      employee={employee}
      onApplyTransactionAction={onApplyTransactionAction}
      changeLogs={transactionChangeLogs}
      accountBalance={accountBalance}
      currencyId={currencyId}
      balanceIsLoading={accountBalanceIsLoading || accountBalanceIsFetching}
      updateBalance={getAccountBalanceAction}
      updateExternalBalance={getExternalAccountBalanceAction}
      updatePspPartnerBalance={getPspPartnerAccountBalanceAction}
      pspPartnerAccountBalance={pspPartnerAccountBalance}
      pspPartnerBalanceIsLoading={
        pspPartnerAccountBalanceIsLoading || pspPartnerAccountBalanceIsFetching
      }
      isDirty={isDirty}
      checkInProgress={checkInProgress}
      signaturePopupVisible={signaturePopupVisible}
      sendCode={onSendCodeAction}
      validateCode={validateCodeAction}
      transactionId={transactionId}
      validateCodeInProgress={
        validateCodeInProgress || createSignatureInProgress
      }
      signatures={signatures}
      errorSidepanel={errorMessage}
      rejectPopupVisible={rejectPopupVisible}
      setRejectPopupVisible={setRejectPopupVisible}
      onReject={onReject}
      rejectInProgress={
        changeStatusIsLoading || applyTransactionsWarningIsLoading
      }
      updateStatusIsLoading={
        statusChangeInProgress || applyTransactionsWarningIsLoading
      }
      isLoadingStatuses={isFetchingStatuses}
      transferDate={transferDate}
      errorMessageSignature={errorMessageSignature}
      transactionsTypes={transactionsTypes}
      checkError={
        statusCheckError ||
        (checkStatus === CheckTransactionStatus.RED
          ? 'Transaction could not be completed'
          : undefined)
      }
      transactionCheckError={checkError}
      onSignatureCancelClick={onSignatureCancelClick}
      activePermission={activePermissions}
      onChangeStatusPopupLoading={editIsLoading || changeStatusIsLoading}
      amount={amount}
      setAmount={setAmount}
      createdByOrganizationId={transaction?.createdByOrganizationId}
      latestQueuedStatus={transaction?.latestQueuedStatuses?.baseellaStatus}
      statusChangeError={statusChangeError}
      setValue={setValue}
      applyTransactionsWarningIsLoading={applyTransactionsWarningIsLoading}
      companyOfficials={companyOfficials}
      chats={chats}
      onCreateTransactionChat={onCreateTransactionChat}
      chatCreateInProgress={chatCreateInProgress}
      currentEmployee={currentEmployee}
      createChatWithMessage={createChatWithMessage}
      documents={documents}
      document={documentItem}
      documentFileIsLoading={downloadInProgress || downloadIsFetching}
      downloadInProgress={downloadInProgress || downloadIsFetching}
      imgLink={imgLink}
      onDelete={onDeleteDocument}
      deleteInProgress={deleteDocumentInProgress}
      deleteIsSuccess={deleteDocumentIsSuccess}
      onMove={onMoveDocument}
      moveInProgress={documentCategoryChangeInProgress}
      onDownload={onDownloadDocument}
      sendComment={onSendDocumentComment}
      editComment={onEditComment}
      editCommentInProgress={editCommentInProgress}
      editCommentIsSuccess={editCommentIsSuccess}
      deleteComment={onDeleteComment}
      deleteCommentInProgress={deleteCommentInProgress}
      commentInProgress={commentInProgress}
      statusChangeInProgress={documentStatusChangeInProgress}
      changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
      uploadDocumentErrors={uploadedFilesError}
      uploadDocumentInProgress={fileUploadInProgress || addDocumentInProgress}
      uploadDocumentIsSuccess={fileUploadIsSuccess}
      onUploadDocument={onUploadDocument}
      onDownloadSuccess={downloadFileIsSuccess}
      getTransactionDocument={getTransactionDocumentyIdAction}
      documentIsLoading={documentIsLoading || documentIsFetching}
      glTransactionsIsLoading={glIsLoading}
      glTransactionIsLoading={glTransactionIsLoading || glTransactionIsFetching}
      glTransactions={glTransactions}
      onGlTransactionClick={onGlTransactionClick}
      glTransaction={expandedGLTransaction}
      clearGlTransactionState={() =>
        dispatch(clearExpandedGeneralLedgerTransactionState())
      }
      deleteIsVisible={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_DOCUMENTS_DELETE_ACTION,
      )}
      expandedGLTransactionCreditBalance={expandedGLTransactionCreditBalance}
      expandedGLTransactionDebitBalance={expandedGLTransactionDebitBalance}
      expendedDebitBalanceIsLoading={
        debitBalanceIsLoading || debitBalanceIsFetching
      }
      expendedCreditBalanceIsLoading={
        creditBalanceIsLoading || creditBalanceIsFetching
      }
      deleteTransactionError={deleteTransactionError}
      onDeleteTransactions={onDeleteTransaction}
      deleteTransactionInProgress={deleteTransactionInProgress}
      updateIsLoading={editIsLoading}
      createIsLoading={createInProgress}
      canSignTransaction={currentEmployee?.canSignTransactions}
      deleteTransactionDisabled={
        !activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DELETE_ACTION,
        )
      }
      sendCodeInProgress={sendCodeInProgress}
      balances={balances}
      accountBalancesIsLoading={
        allAccountBalancesIsLoading || allAccountBalancesisFetching
      }
      currenciesList={dictionariesCurrenciesList}
      onDuplicate={onDuplicate}
      duplicate={locationState?.duplicate}
      fields={fields}
      caspList={caspList}
      externalAccounts={externalAccountsList}
      externalAccountsIsLoading={externalAccountsListLoading}
      onSearchExternalAccounts={onSearchExternalAccounts}
      onClearExternalAccounts={onClearExxternalAccounts}
      onGetExternalAccountsListAction={getExternalAccountsListAction}
      externalAccountsPagination={externalAccountsPagination}
      externalAccountsNextPageIsLoading={externalAccountsNextPageIsLoading}
      getTransactionDocuments={getTransationDocumentsAction}
      documentsIsLoading={documentsIsLoading}
      getGeneralLedgerTransactionValues={
        getGeneralLedgerTransactionValuesAction
      }
      getTransactionsChats={getTransactionsChatsAction}
      transactionChatsIsLoading={transactionChatsIsLoading || cofIsLoading}
      getCompanyOfficials={getCompanyOfficialsAction}
      getSignaturesTransactionById={getSignaturesTransactionByIdAction}
      isLoadingSignatures={isLoadingSignatures}
      getIncomingTransactionByIdAml={getIncomingTransactionByIdAmlAction}
      isLoadingAml={isLoadingAml}
      getLogsTransactionById={getLogsTransactionByIdAction}
      isLoadingLogs={isLoadingLogs}
      overdraftEnabled={overdraftEnabled}
      recipientAccountsWithInfo={accountsList}
      pspPartnersAccountsList={pspPartnersAccountsList}
      pspPartnerAccountBalanceInfo={pspPartnerAccountBalanceInfo}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      changeLogsIsLoading={changeLogsIsLoading}
      users={users}
      transactionChangeLogsPagination={transactionChangeLogsPagination}
      changeLogsIsFetching={changeLogsIsFetching}
      onResetTransactionWarningAction={onResetTransactionWarningAction}
      resetTransactionsWarningIsLoading={resetTransactionsWarningIsLoading}
      resetTransactionsWarningIsSuccess={resetTransactionsWarningIsSuccess}
      documentsPagination={documentsPagination}
      documentsIsFetching={documentsIsFetching}
      attachedDocumentsErrors={attachedDocumentsErrors}
      uploadedDocumentsErrors={uploadedDocumentsErrors}
      transactionComments={transactionComments}
      transactionCommentsPagination={transactionCommentsPagination}
      getTransactionCommentsAction={getTransactionCommentsAction}
      commentsIsLoading={commentsIsLoading}
      commentsIsFetching={commentsIsFetching}
      onAddCommentToTransaction={onAddCommentToTransaction}
      addCommentInProgress={addCommentInProgress}
      addCommentIsSuccess={addCommentIsSuccess}
      onEditCommentInTransaction={onEditCommentInTransaction}
      editTransactionCommentInProgress={editTransactionCommentInProgress}
      editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
      onDeleteCommentInTransaction={onDeleteCommentInTransaction}
      deleteCommentInTransactionInProgress={
        deleteCommentInTransactionInProgress
      }
      deleteCommentInTranscationIsSuccess={deleteCommentInTranscationIsSuccess}
      getIncomingTransactionWarningById={
        getIncomingTransactionWarningByIdAction
      }
      isWarningByIdFetching={isWarningByIdFetching}
      isWarningByIdLoading={isWarningByIdLoading}
      warningsInfo={warningsInfo}
      selectedWarningId={selectedWarningId}
      pspPartnerAccountsPagination={pagination}
      pspPartnerAccountsNextPageIsLoading={nextPageIsLoading}
      pspPartnerAccountsOnScroll={onScroll}
      warningActionError={warningActionError}
      clearErrors={clearErrors}
      deleteDisabled={ffSettings?.ffDisableTxGlRemoval}
      withTime={{
        transferDateWithTime,
        postingDateWithTime,
        accpetedInWithTime,
      }}
      setWithTime={{
        setTransferDateWithTime,
        setPostingDateWithTime,
        setAcceptedInWithTime,
      }}
    />
  );
};

export default IncomingTransaction;
